import React, { useEffect, useState, useCallback } from "react";
import { useAdmin } from "../context/AdminContext";
import { toast } from "sonner";
import FileUploadField from "../components/FileUploadField";
import { LogOut, Trash2, Plus, ShieldCheck, MessageSquare, Ban, Mail, Send, Users, Inbox, Calendar, Mic2, Handshake, Eye, EyeOff, Newspaper, Pencil, X as XIcon, Radio, Settings } from "lucide-react";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const LoginView = () => {
  const { login } = useAdmin();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    const res = await login(password);
    setSubmitting(false);
    if (!res.ok) toast.error(res.error || "Login failed");
    else toast.success("Signed in");
  };

  return (
    <div className="min-h-[60vh] grid place-items-center px-5">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-jet-surface border border-gold/30 rounded-lg p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 grid place-items-center rounded border-gold-gradient">
            <ShieldCheck className="w-5 h-5 text-gold" />
          </div>
          <div>
            <div className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
              GFN Admin
            </div>
            <div className="font-display text-2xl text-white leading-none">Sign In</div>
          </div>
        </div>
        <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
          Admin Password
        </label>
        <input
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
          autoFocus
        />
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={submitting}
          className="mt-5 w-full bg-gold hover:bg-gold-light disabled:opacity-50 text-black font-condensed uppercase tracking-widest text-xs px-4 py-3 rounded-md transition-colors"
        >
          {submitting ? "Signing in…" : "Enter Booth"}
        </button>
      </form>
    </div>
  );
};

const AdminPanel = () => {
  const { authFetch, logout } = useAdmin();
  const [messages, setMessages] = useState([]);
  const [words, setWords] = useState([]);
  const [newWord, setNewWord] = useState("");
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [nlSubject, setNlSubject] = useState("");
  const [nlHtml, setNlHtml] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [shows, setShows] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [newSponsor, setNewSponsor] = useState({ name: "", logo_url: "", url: "" });
  const [articles, setArticles] = useState([]);
  const [editArticleId, setEditArticleId] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: "",
    body: "",
    category: "",
    image_url: "",
    external_url: "",
    published: true,
  });
  const [savingArticle, setSavingArticle] = useState(false);
  const [djProfiles, setDjProfiles] = useState([]);
  const [editDjId, setEditDjId] = useState(null);
  const [djForm, setDjForm] = useState({ name: "", image_url: "", bio: "", published: true });
  const [savingDj, setSavingDj] = useState(false);
  const [settings, setSettings] = useState({ ticker_speed: "normal" });
  const [newShow, setNewShow] = useState({
    name: "",
    dj: "",
    description: "",
    day_of_week: 1,
    start_time: "19:00",
    end_time: "21:00",
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, wRes, sRes, cRes, shRes, sgRes, spRes, arRes, djRes, stRes] = await Promise.all([
        authFetch("/api/admin/chat/messages"),
        authFetch("/api/admin/banned-words"),
        authFetch("/api/admin/newsletter/subscribers"),
        authFetch("/api/admin/contact/messages"),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shows`),
        authFetch("/api/admin/show-suggestions"),
        authFetch("/api/admin/sponsors"),
        authFetch("/api/admin/news"),
        authFetch("/api/admin/djs"),
        authFetch("/api/admin/settings"),
      ]);
      if (mRes.ok) setMessages(await mRes.json());
      if (wRes.ok) setWords(await wRes.json());
      if (sRes.ok) setSubs(await sRes.json());
      if (cRes.ok) setContacts(await cRes.json());
      if (shRes.ok) setShows(await shRes.json());
      if (sgRes.ok) setSuggestions(await sgRes.json());
      if (spRes.ok) setSponsors(await spRes.json());
      if (arRes.ok) setArticles(await arRes.json());
      if (djRes.ok) setDjProfiles(await djRes.json());
      if (stRes.ok) setSettings(await stRes.json());
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 8000);
    return () => clearInterval(id);
  }, [loadAll]);

  const deleteMessage = async (id) => {
    const res = await authFetch(`/api/admin/chat/messages/${id}`, { method: "DELETE" });
    if (res.ok) {
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message deleted");
    } else {
      toast.error("Failed to delete");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear ALL chat messages?")) return;
    const res = await authFetch("/api/admin/chat/messages", { method: "DELETE" });
    if (res.ok) {
      setMessages([]);
      toast.success("Chat cleared");
    }
  };

  const addWord = async (e) => {
    e.preventDefault();
    const word = newWord.trim().toLowerCase();
    if (!word) return;
    const res = await authFetch("/api/admin/banned-words", {
      method: "POST",
      body: JSON.stringify({ word }),
    });
    if (res.ok) {
      const bw = await res.json();
      setWords((prev) => (prev.some((w) => w.id === bw.id) ? prev : [bw, ...prev]));
      setNewWord("");
      toast.success(`Added "${word}"`);
    } else {
      toast.error("Failed to add word");
    }
  };

  const deleteWord = async (id, word) => {
    const res = await authFetch(`/api/admin/banned-words/${id}`, { method: "DELETE" });
    if (res.ok) {
      setWords((prev) => prev.filter((w) => w.id !== id));
      toast.success(`Removed "${word}"`);
    }
  };

  const deleteSubscriber = async (id, email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    const res = await authFetch(`/api/admin/newsletter/subscribers/${id}`, { method: "DELETE" });
    if (res.ok) {
      setSubs((prev) => prev.filter((s) => s.id !== id));
      toast.success(`Removed ${email}`);
    }
  };

  const sendBroadcast = async (e) => {    e.preventDefault();
    if (!nlSubject.trim() || !nlHtml.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    if (!window.confirm(`Send this to ${subs.filter(s => s.status === "active").length} subscribers?`)) return;
    setBroadcasting(true);
    try {
      const res = await authFetch("/api/admin/newsletter/broadcast", {
        method: "POST",
        body: JSON.stringify({ subject: nlSubject, html: nlHtml }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Sent to ${data.sent} / ${data.total}${data.failed ? ` — ${data.failed} failed` : ""}`);
        setNlSubject("");
        setNlHtml("");
      } else {
        toast.error(data.detail || "Broadcast failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setBroadcasting(false);
    }
  };

  const markContact = async (id, status) => {
    const res = await authFetch(`/api/admin/contact/messages/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this contact message?")) return;
    const res = await authFetch(`/api/admin/contact/messages/${id}`, { method: "DELETE" });
    if (res.ok) {
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Deleted");
    }
  };

  const addShow = async (e) => {
    e.preventDefault();
    if (!newShow.name.trim()) return toast.error("Show name required");
    const res = await authFetch("/api/admin/shows", {
      method: "POST",
      body: JSON.stringify({
        ...newShow,
        day_of_week: Number(newShow.day_of_week),
      }),
    });
    if (res.ok) {
      const s = await res.json();
      setShows((prev) => [...prev, s].sort((a, b) => (a.day_of_week - b.day_of_week) || a.start_time.localeCompare(b.start_time)));
      setNewShow({ name: "", dj: "", description: "", day_of_week: 1, start_time: "19:00", end_time: "21:00" });
      toast.success("Show added");
    } else {
      const j = await res.json().catch(() => ({}));
      toast.error(j.detail || "Failed to add");
    }
  };

  const deleteShow = async (id) => {
    if (!window.confirm("Delete this show?")) return;
    const res = await authFetch(`/api/admin/shows/${id}`, { method: "DELETE" });
    if (res.ok) {
      setShows((prev) => prev.filter((s) => s.id !== id));
      toast.success("Deleted");
    }
  };

  const deleteSuggestion = async (id) => {
    const res = await authFetch(`/api/admin/show-suggestions/${id}`, { method: "DELETE" });
    if (res.ok) {
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      toast.success("Removed");
    }
  };

  const addSponsor = async (e) => {
    e.preventDefault();
    if (!newSponsor.name.trim()) return toast.error("Sponsor name required");
    const res = await authFetch("/api/admin/sponsors", {
      method: "POST",
      body: JSON.stringify({ ...newSponsor, active: true }),
    });
    if (res.ok) {
      const s = await res.json();
      setSponsors((prev) => [...prev, s]);
      setNewSponsor({ name: "", logo_url: "", url: "" });
      toast.success("Sponsor added");
    } else {
      toast.error("Failed to add sponsor");
    }
  };

  const toggleSponsor = async (sp) => {
    const res = await authFetch(`/api/admin/sponsors/${sp.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        name: sp.name,
        logo_url: sp.logo_url,
        url: sp.url,
        active: !sp.active,
        sort_order: sp.sort_order,
      }),
    });
    if (res.ok) {
      const updated = await res.json();
      setSponsors((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    }
  };

  const deleteSponsor = async (id) => {
    if (!window.confirm("Delete this sponsor?")) return;
    const res = await authFetch(`/api/admin/sponsors/${id}`, { method: "DELETE" });
    if (res.ok) {
      setSponsors((prev) => prev.filter((s) => s.id !== id));
      toast.success("Deleted");
    }
  };

  const resetArticleForm = () => {
    setEditArticleId(null);
    setArticleForm({
      title: "",
      body: "",
      category: "",
      image_url: "",
      external_url: "",
      published: true,
    });
  };

  const submitArticle = async (e) => {
    e.preventDefault();
    if (!articleForm.title.trim() || !articleForm.body.trim()) {
      return toast.error("Title and body are required");
    }
    setSavingArticle(true);
    try {
      const url = editArticleId
        ? `/api/admin/news/${editArticleId}`
        : "/api/admin/news";
      const method = editArticleId ? "PATCH" : "POST";
      const res = await authFetch(url, {
        method,
        body: JSON.stringify(articleForm),
      });
      if (res.ok) {
        const saved = await res.json();
        setArticles((prev) => {
          if (editArticleId) {
            return prev.map((a) => (a.id === saved.id ? saved : a));
          }
          return [saved, ...prev];
        });
        resetArticleForm();
        toast.success(editArticleId ? "Article updated" : "Article published");
      } else {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Failed to save article");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSavingArticle(false);
    }
  };

  const startEditArticle = (a) => {
    setEditArticleId(a.id);
    setArticleForm({
      title: a.title || "",
      body: a.body || "",
      category: a.category || "",
      image_url: a.image_url || "",
      external_url: a.external_url || "",
      published: a.published !== false,
    });
    // Scroll form into view
    setTimeout(() => {
      document.getElementById("admin-news-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const togglePublishArticle = async (a) => {
    const res = await authFetch(`/api/admin/news/${a.id}`, {
      method: "PATCH",
      body: JSON.stringify({ ...a, published: !a.published }),
    });
    if (res.ok) {
      const saved = await res.json();
      setArticles((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    const res = await authFetch(`/api/admin/news/${id}`, { method: "DELETE" });
    if (res.ok) {
      setArticles((prev) => prev.filter((a) => a.id !== id));
      if (editArticleId === id) resetArticleForm();
      toast.success("Deleted");
    }
  };

  // ---- DJ Profiles ----
  const resetDjForm = () => {
    setEditDjId(null);
    setDjForm({ name: "", image_url: "", bio: "", published: true });
  };

  const submitDj = async (e) => {
    e.preventDefault();
    if (!djForm.name.trim()) return toast.error("DJ name required");
    setSavingDj(true);
    try {
      const url = editDjId ? `/api/admin/djs/${editDjId}` : "/api/admin/djs";
      const method = editDjId ? "PATCH" : "POST";
      const res = await authFetch(url, { method, body: JSON.stringify(djForm) });
      if (res.ok) {
        const saved = await res.json();
        setDjProfiles((prev) => {
          if (editDjId) return prev.map((d) => (d.id === saved.id ? saved : d));
          return [...prev, saved].sort((a, b) => a.name.localeCompare(b.name));
        });
        resetDjForm();
        toast.success(editDjId ? "DJ updated" : "DJ added");
      } else {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Failed to save DJ");
      }
    } finally {
      setSavingDj(false);
    }
  };

  const startEditDj = (d) => {
    setEditDjId(d.id);
    setDjForm({
      name: d.name || "",
      image_url: d.image_url || "",
      bio: d.bio || "",
      published: d.published !== false,
    });
    setTimeout(() => {
      document.getElementById("admin-dj-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const deleteDj = async (id) => {
    if (!window.confirm("Delete this DJ profile?")) return;
    const res = await authFetch(`/api/admin/djs/${id}`, { method: "DELETE" });
    if (res.ok) {
      setDjProfiles((prev) => prev.filter((d) => d.id !== id));
      if (editDjId === id) resetDjForm();
      toast.success("Deleted");
    }
  };

  // ---- Settings ----
  const updateTickerSpeed = async (speed) => {
    const res = await authFetch("/api/admin/settings", {
      method: "PATCH",
      body: JSON.stringify({ ticker_speed: speed }),
    });
    if (res.ok) {
      setSettings(await res.json());
      toast.success(`Ticker speed: ${speed}`);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10">
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
            Admin Booth
          </div>
          <h1 className="font-display text-4xl md:text-5xl uppercase text-white leading-none mt-1">
            Moderation Panel
          </h1>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          className="inline-flex items-center gap-2 border border-gold/40 hover:border-gold text-gold px-4 py-2 rounded-full font-condensed uppercase tracking-widest text-[10px] transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </header>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Messages */}
        <section
          data-testid="admin-messages-section"
          className="bg-jet-surface border border-gold/20 rounded-lg overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-900">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-gold" />
              <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
                Live Chat ({messages.length})
              </h2>
            </div>
            <button
              data-testid="admin-clear-btn"
              onClick={clearAll}
              className="font-condensed uppercase tracking-widest text-[10px] text-crimson-live hover:text-red-400"
            >
              Clear All
            </button>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-neutral-900">
            {loading && messages.length === 0 && (
              <div className="p-6 text-neutral-500 text-sm">Loading…</div>
            )}
            {!loading && messages.length === 0 && (
              <div className="p-8 text-neutral-500 text-sm text-center">
                No messages yet.
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`admin-msg-${m.id}`}
                className="flex items-start gap-4 px-5 py-3 hover:bg-neutral-900/40"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-condensed uppercase tracking-widest text-[10px] text-gold">
                      {m.name}
                    </span>
                    <span className="text-neutral-600 text-[10px]">
                      {new Date(m.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-100 break-words">{m.message}</div>
                </div>
                <button
                  data-testid={`admin-delete-msg-${m.id}`}
                  onClick={() => deleteMessage(m.id)}
                  aria-label="Delete message"
                  className="text-neutral-500 hover:text-crimson-live shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Banned words */}
        <section
          data-testid="admin-banned-section"
          className="bg-jet-surface border border-gold/20 rounded-lg overflow-hidden h-fit"
        >
          <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
            <Ban className="w-4 h-4 text-gold" />
            <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
              Banned Words ({words.length})
            </h2>
          </div>
          <form onSubmit={addWord} className="p-4 border-b border-neutral-900 flex gap-2">
            <input
              data-testid="admin-new-word-input"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              placeholder="Add a banned word or phrase"
              maxLength={64}
              className="flex-1 bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
            />
            <button
              data-testid="admin-add-word-btn"
              type="submit"
              className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-gold hover:bg-gold-light text-black transition-colors"
              aria-label="Add"
            >
              <Plus className="w-4 h-4" />
            </button>
          </form>
          <div className="max-h-[60vh] overflow-y-auto">
            {words.length === 0 ? (
              <div className="p-6 text-neutral-500 text-sm text-center">
                No banned words yet. Add some to auto-block matching messages.
              </div>
            ) : (
              <ul className="divide-y divide-neutral-900">
                {words.map((w) => (
                  <li
                    key={w.id}
                    data-testid={`admin-word-${w.id}`}
                    className="flex items-center justify-between px-5 py-2.5 hover:bg-neutral-900/40"
                  >
                    <span className="font-body text-sm text-neutral-100 break-all">
                      {w.word}
                    </span>
                    <button
                      data-testid={`admin-remove-word-${w.id}`}
                      onClick={() => deleteWord(w.id, w.word)}
                      aria-label="Remove word"
                      className="text-neutral-500 hover:text-crimson-live"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* Newsletter (Broadcast + Subscribers) */}
      <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-6">
        <section
          data-testid="admin-newsletter-section"
          className="bg-jet-surface border border-gold/20 rounded-lg overflow-hidden"
        >
          <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
            <Mail className="w-4 h-4 text-gold" />
            <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
              Send Show Alert (Newsletter)
            </h2>
          </div>
          <form onSubmit={sendBroadcast} className="p-4 space-y-3">
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                Subject
              </label>
              <input
                data-testid="admin-nl-subject"
                value={nlSubject}
                onChange={(e) => setNlSubject(e.target.value)}
                placeholder="Tonight on GFN Soul NYC…"
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
              />
            </div>
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                Message (HTML allowed)
              </label>
              <textarea
                data-testid="admin-nl-html"
                value={nlHtml}
                onChange={(e) => setNlHtml(e.target.value)}
                rows={7}
                placeholder="<h2>What's playing</h2><p>Tune in at 8 PM ET for a live set with DJ …</p>"
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none font-mono"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="font-condensed uppercase tracking-widest text-[10px] text-neutral-400">
                Recipients: {subs.filter((s) => s.status === "active").length}
              </div>
              <button
                data-testid="admin-nl-send"
                type="submit"
                disabled={broadcasting}
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light disabled:opacity-50 text-black font-condensed uppercase tracking-widest text-xs px-5 py-2.5 rounded-md transition-colors"
              >
                <Send className="w-4 h-4" />
                {broadcasting ? "Sending…" : "Send Blast"}
              </button>
            </div>
            <p className="font-body text-[11px] text-neutral-500">
              From: <span className="text-neutral-300">GFN Soul NYC &lt;hello@gfnsoulglobalent.com&gt;</span>
              . Requires a valid <code className="text-gold">RESEND_API_KEY</code> in the backend .env, and the
              sending domain to be verified in your Resend account.
            </p>
          </form>
        </section>

        <section
          data-testid="admin-subscribers-section"
          className="bg-jet-surface border border-gold/20 rounded-lg overflow-hidden h-fit"
        >
          <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
            <Users className="w-4 h-4 text-gold" />
            <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
              Subscribers ({subs.length})
            </h2>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {subs.length === 0 ? (
              <div className="p-6 text-neutral-500 text-sm text-center">
                No subscribers yet.
              </div>
            ) : (
              <ul className="divide-y divide-neutral-900">
                {subs.map((s) => (
                  <li
                    key={s.id}
                    data-testid={`admin-sub-${s.id}`}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-neutral-900/40"
                  >
                    <div className="min-w-0">
                      <div className="font-body text-sm text-neutral-100 truncate">
                        {s.email}
                      </div>
                      {s.name && (
                        <div className="font-body text-xs text-neutral-500 truncate">{s.name}</div>
                      )}
                    </div>
                    <button
                      data-testid={`admin-remove-sub-${s.id}`}
                      onClick={() => deleteSubscriber(s.id, s.email)}
                      aria-label="Remove subscriber"
                      className="text-neutral-500 hover:text-crimson-live shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* Contact submissions */}
      <section
        data-testid="admin-contact-section"
        className="mt-8 bg-jet-surface border border-gold/20 rounded-lg overflow-hidden"
      >
        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
          <Inbox className="w-4 h-4 text-gold" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            Contact Inbox ({contacts.length})
          </h2>
        </div>
        <div className="max-h-[70vh] overflow-y-auto divide-y divide-neutral-900">
          {contacts.length === 0 ? (
            <div className="p-8 text-neutral-500 text-sm text-center">
              No contact messages yet.
            </div>
          ) : (
            contacts.map((c) => (
              <article
                key={c.id}
                data-testid={`admin-contact-${c.id}`}
                className="px-5 py-4 hover:bg-neutral-900/40"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`font-condensed uppercase tracking-widest text-[10px] px-2 py-0.5 rounded ${
                          c.status === "new"
                            ? "bg-crimson/20 text-crimson-live"
                            : c.status === "read"
                            ? "bg-gold/20 text-gold"
                            : "bg-neutral-800 text-neutral-400"
                        }`}
                      >
                        {c.status}
                      </span>
                      <span className="font-condensed uppercase tracking-widest text-[11px] text-gold">
                        {c.name}
                      </span>
                      <a
                        href={`mailto:${c.email}`}
                        className="text-neutral-400 hover:text-gold text-xs"
                      >
                        {c.email}
                      </a>
                      <span className="text-neutral-600 text-[10px]">
                        {new Date(c.submitted_at).toLocaleString()}
                      </span>
                    </div>
                    {c.subject && (
                      <div className="mt-1 font-display text-lg text-white leading-tight">
                        {c.subject}
                      </div>
                    )}
                    <div className="mt-1 text-sm text-neutral-200 whitespace-pre-wrap break-words">
                      {c.message}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {c.status !== "read" && (
                      <button
                        data-testid={`admin-contact-mark-read-${c.id}`}
                        onClick={() => markContact(c.id, "read")}
                        className="font-condensed uppercase tracking-widest text-[10px] text-gold hover:text-gold-light px-2 py-1"
                      >
                        Mark Read
                      </button>
                    )}
                    {c.status !== "archived" && (
                      <button
                        data-testid={`admin-contact-archive-${c.id}`}
                        onClick={() => markContact(c.id, "archived")}
                        className="font-condensed uppercase tracking-widest text-[10px] text-neutral-400 hover:text-gold px-2 py-1"
                      >
                        Archive
                      </button>
                    )}
                    <button
                      data-testid={`admin-contact-delete-${c.id}`}
                      onClick={() => deleteContact(c.id)}
                      aria-label="Delete"
                      className="text-neutral-500 hover:text-crimson-live px-2 py-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      {/* DJ Schedule + Suggestions */}
      <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <section
          data-testid="admin-shows-section"
          className="bg-jet-surface border border-gold/20 rounded-lg overflow-hidden"
        >
          <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
            <Calendar className="w-4 h-4 text-gold" />
            <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
              DJ Schedule ({shows.length})
            </h2>
          </div>
          <form onSubmit={addShow} className="p-4 grid md:grid-cols-2 gap-3 border-b border-neutral-900">
            <input
              data-testid="admin-show-name"
              value={newShow.name}
              onChange={(e) => setNewShow({ ...newShow, name: e.target.value })}
              placeholder="Show name"
              className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none md:col-span-2"
            />
            <input
              data-testid="admin-show-dj"
              value={newShow.dj}
              onChange={(e) => setNewShow({ ...newShow, dj: e.target.value })}
              placeholder="Host / DJ"
              className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
            />
            <select
              data-testid="admin-show-day"
              value={newShow.day_of_week}
              onChange={(e) => setNewShow({ ...newShow, day_of_week: e.target.value })}
              className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
            >
              {DOW.map((d, i) => (
                <option key={d} value={i}>{d} (ET)</option>
              ))}
            </select>
            <input
              data-testid="admin-show-start"
              type="time"
              value={newShow.start_time}
              onChange={(e) => setNewShow({ ...newShow, start_time: e.target.value })}
              className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
            />
            <input
              data-testid="admin-show-end"
              type="time"
              value={newShow.end_time}
              onChange={(e) => setNewShow({ ...newShow, end_time: e.target.value })}
              className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
            />
            <textarea
              data-testid="admin-show-description"
              value={newShow.description}
              onChange={(e) => setNewShow({ ...newShow, description: e.target.value })}
              placeholder="Description (optional)"
              rows={2}
              className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none md:col-span-2 resize-y"
            />
            <button
              data-testid="admin-show-add"
              type="submit"
              className="md:col-span-2 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-5 py-2.5 rounded-md transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Show
            </button>
          </form>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-neutral-900">
            {shows.length === 0 ? (
              <div className="p-6 text-center text-neutral-500 text-sm">
                No shows yet. Add your first show above.
              </div>
            ) : (
              shows.map((s) => (
                <div
                  key={s.id}
                  data-testid={`admin-show-${s.id}`}
                  className="flex items-start justify-between gap-3 px-5 py-3 hover:bg-neutral-900/40"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-condensed uppercase tracking-widest text-[10px] text-gold">
                        {DOW[s.day_of_week]} · {s.start_time}–{s.end_time} ET
                      </span>
                    </div>
                    <div className="mt-0.5 font-display text-lg text-white uppercase leading-tight">
                      {s.name}
                    </div>
                    {s.dj && (
                      <div className="font-body text-xs text-gold/80">With {s.dj}</div>
                    )}
                    {s.description && (
                      <div className="font-body text-xs text-neutral-500 mt-0.5">{s.description}</div>
                    )}
                  </div>
                  <button
                    data-testid={`admin-show-delete-${s.id}`}
                    onClick={() => deleteShow(s.id)}
                    aria-label="Delete show"
                    className="text-neutral-500 hover:text-crimson-live shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </section>

        <section
          data-testid="admin-show-suggestions-section"
          className="bg-jet-surface border border-gold/20 rounded-lg overflow-hidden h-fit"
        >
          <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
            <Mic2 className="w-4 h-4 text-gold" />
            <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
              Show Ideas ({suggestions.length})
            </h2>
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-neutral-900">
            {suggestions.length === 0 ? (
              <div className="p-6 text-center text-neutral-500 text-sm">
                No suggestions yet.
              </div>
            ) : (
              suggestions.map((sg) => (
                <div
                  key={sg.id}
                  data-testid={`admin-suggestion-${sg.id}`}
                  className="px-5 py-3 hover:bg-neutral-900/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-base text-white uppercase leading-tight">
                        {sg.show_name}
                      </div>
                      {sg.dj && (
                        <div className="font-body text-xs text-gold/80">With {sg.dj}</div>
                      )}
                      {sg.preferred_slot && (
                        <div className="font-condensed uppercase tracking-widest text-[10px] text-neutral-500 mt-0.5">
                          Slot: {sg.preferred_slot}
                        </div>
                      )}
                      {sg.description && (
                        <div className="font-body text-xs text-neutral-400 mt-1">{sg.description}</div>
                      )}
                      {sg.contact_email && (
                        <a href={`mailto:${sg.contact_email}`} className="mt-1 inline-block text-[11px] text-gold hover:underline">
                          {sg.contact_email}
                        </a>
                      )}
                    </div>
                    <button
                      data-testid={`admin-suggestion-delete-${sg.id}`}
                      onClick={() => deleteSuggestion(sg.id)}
                      aria-label="Delete suggestion"
                      className="text-neutral-500 hover:text-crimson-live shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* News articles */}
      <section
        data-testid="admin-news-section"
        className="mt-8 bg-jet-surface border border-gold/20 rounded-lg overflow-hidden"
      >
        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
          <Newspaper className="w-4 h-4 text-gold" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            News Articles ({articles.length})
          </h2>
        </div>
        <form
          id="admin-news-form"
          onSubmit={submitArticle}
          className="p-4 grid md:grid-cols-2 gap-3 border-b border-neutral-900"
        >
          <div className="md:col-span-2 flex items-center justify-between">
            <div className="font-condensed uppercase tracking-widest text-[11px] text-gold">
              {editArticleId ? "Editing article" : "New article"}
            </div>
            {editArticleId && (
              <button
                type="button"
                data-testid="admin-news-cancel-edit"
                onClick={resetArticleForm}
                className="inline-flex items-center gap-1 text-neutral-400 hover:text-gold font-condensed uppercase tracking-widest text-[10px]"
              >
                <XIcon className="w-3.5 h-3.5" />
                Cancel edit
              </button>
            )}
          </div>
          <input
            data-testid="admin-news-title"
            value={articleForm.title}
            onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
            placeholder="Article title"
            maxLength={200}
            className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none md:col-span-2"
          />
          <input
            data-testid="admin-news-category"
            value={articleForm.category}
            onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}
            placeholder="Category (Music / Community / Announcement…)"
            maxLength={60}
            className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
          />
          <input
            data-testid="admin-news-external"
            value={articleForm.external_url}
            onChange={(e) => setArticleForm({ ...articleForm, external_url: e.target.value })}
            placeholder="External source URL (optional)"
            className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
          />
          <div className="md:col-span-2">
            <FileUploadField
              label="Cover image (optional)"
              value={articleForm.image_url}
              onChange={(url) => setArticleForm({ ...articleForm, image_url: url })}
              authFetch={authFetch}
              testIdPrefix="admin-news-image"
            />
          </div>
          <textarea
            data-testid="admin-news-body"
            value={articleForm.body}
            onChange={(e) => setArticleForm({ ...articleForm, body: e.target.value })}
            placeholder="Write the story… (line breaks preserved)"
            rows={8}
            maxLength={20000}
            className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none md:col-span-2 resize-y font-body"
          />
          <label className="md:col-span-2 flex items-center gap-2 text-neutral-300 text-sm">
            <input
              data-testid="admin-news-published"
              type="checkbox"
              checked={articleForm.published}
              onChange={(e) => setArticleForm({ ...articleForm, published: e.target.checked })}
              className="w-4 h-4 accent-yellow-500"
            />
            Published (visible on /news)
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button
              data-testid="admin-news-save"
              type="submit"
              disabled={savingArticle}
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light disabled:opacity-50 text-black font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-md transition-colors"
            >
              {editArticleId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {savingArticle
                ? "Saving…"
                : editArticleId
                ? "Update Article"
                : "Publish Article"}
            </button>
          </div>
        </form>

        <div className="max-h-[70vh] overflow-y-auto divide-y divide-neutral-900">
          {articles.length === 0 ? (
            <div className="p-6 text-center text-neutral-500 text-sm">
              No articles yet. Post your first story above — it'll show up on the News page instantly.
            </div>
          ) : (
            articles.map((a) => (
              <div
                key={a.id}
                data-testid={`admin-news-${a.id}`}
                className="flex items-start gap-4 px-5 py-4 hover:bg-neutral-900/40"
              >
                {a.image_url && (
                  <div className="w-24 h-16 shrink-0 rounded border border-neutral-800 bg-black overflow-hidden">
                    <img src={a.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`font-condensed uppercase tracking-widest text-[10px] px-2 py-0.5 rounded ${
                        a.published
                          ? "bg-crimson/20 text-crimson-live"
                          : "bg-neutral-800 text-neutral-400"
                      }`}
                    >
                      {a.published ? "● Live" : "○ Draft"}
                    </span>
                    {a.category && (
                      <span className="font-condensed uppercase tracking-widest text-[10px] text-gold">
                        {a.category}
                      </span>
                    )}
                    <span className="text-neutral-600 text-[10px]">
                      {new Date(a.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1 font-display text-lg text-white uppercase leading-tight">
                    {a.title}
                  </div>
                  <div className="mt-1 font-body text-xs text-neutral-400 line-clamp-2">
                    {a.body}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    data-testid={`admin-news-toggle-${a.id}`}
                    onClick={() => togglePublishArticle(a)}
                    aria-label={a.published ? "Unpublish" : "Publish"}
                    title={a.published ? "Unpublish" : "Publish"}
                    className="text-neutral-400 hover:text-gold p-2"
                  >
                    {a.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    data-testid={`admin-news-edit-${a.id}`}
                    onClick={() => startEditArticle(a)}
                    aria-label="Edit"
                    title="Edit"
                    className="text-neutral-400 hover:text-gold p-2"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`admin-news-delete-${a.id}`}
                    onClick={() => deleteArticle(a.id)}
                    aria-label="Delete"
                    title="Delete"
                    className="text-neutral-500 hover:text-crimson-live p-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* DJ Profiles */}
      <section
        data-testid="admin-djs-section"
        className="mt-8 bg-jet-surface border border-gold/20 rounded-lg overflow-hidden"
      >
        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
          <Radio className="w-4 h-4 text-gold" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            DJ Profiles ({djProfiles.length})
          </h2>
        </div>
        <form
          id="admin-dj-form"
          onSubmit={submitDj}
          className="p-4 grid md:grid-cols-2 gap-4 border-b border-neutral-900"
        >
          <div className="md:col-span-2 flex items-center justify-between">
            <div className="font-condensed uppercase tracking-widest text-[11px] text-gold">
              {editDjId ? "Editing DJ" : "New DJ"}
            </div>
            {editDjId && (
              <button
                type="button"
                data-testid="admin-dj-cancel-edit"
                onClick={resetDjForm}
                className="inline-flex items-center gap-1 text-neutral-400 hover:text-gold font-condensed uppercase tracking-widest text-[10px]"
              >
                <XIcon className="w-3.5 h-3.5" />
                Cancel edit
              </button>
            )}
          </div>
          <input
            data-testid="admin-dj-name"
            value={djForm.name}
            onChange={(e) => setDjForm({ ...djForm, name: e.target.value })}
            placeholder="DJ name (matches schedule DJ field, e.g. DJ Wayne G)"
            maxLength={80}
            className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none md:col-span-2"
          />
          <div className="md:col-span-2">
            <FileUploadField
              label="Portrait photo"
              value={djForm.image_url}
              onChange={(url) => setDjForm({ ...djForm, image_url: url })}
              authFetch={authFetch}
              testIdPrefix="admin-dj-image"
            />
          </div>
          <textarea
            data-testid="admin-dj-bio"
            value={djForm.bio}
            onChange={(e) => setDjForm({ ...djForm, bio: e.target.value })}
            placeholder="Short bio (optional)"
            rows={3}
            maxLength={2000}
            className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none md:col-span-2 resize-y font-body"
          />
          <label className="md:col-span-2 flex items-center gap-2 text-neutral-300 text-sm">
            <input
              data-testid="admin-dj-published"
              type="checkbox"
              checked={djForm.published}
              onChange={(e) => setDjForm({ ...djForm, published: e.target.checked })}
              className="w-4 h-4 accent-yellow-500"
            />
            Published (shown on About page)
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button
              data-testid="admin-dj-save"
              type="submit"
              disabled={savingDj}
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light disabled:opacity-50 text-black font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-md transition-colors"
            >
              {editDjId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {savingDj ? "Saving…" : editDjId ? "Update DJ" : "Add DJ"}
            </button>
          </div>
        </form>
        <div className="max-h-[70vh] overflow-y-auto divide-y divide-neutral-900">
          {djProfiles.length === 0 ? (
            <div className="p-6 text-center text-neutral-500 text-sm">
              No DJ profiles yet. Add photos + bios above — they'll appear on the About page.
            </div>
          ) : (
            djProfiles.map((d) => (
              <div
                key={d.id}
                data-testid={`admin-dj-${d.id}`}
                className="flex items-start gap-4 px-5 py-4 hover:bg-neutral-900/40"
              >
                <div className="w-16 h-16 shrink-0 rounded-full border border-neutral-800 bg-black overflow-hidden">
                  {d.image_url ? (
                    <img
                      src={d.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${d.image_url}` : d.image_url}
                      alt={d.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center font-condensed uppercase text-[10px] text-neutral-500">
                      No pic
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`font-condensed uppercase tracking-widest text-[10px] px-2 py-0.5 rounded ${
                        d.published ? "bg-crimson/20 text-crimson-live" : "bg-neutral-800 text-neutral-400"
                      }`}
                    >
                      {d.published ? "● Live" : "○ Hidden"}
                    </span>
                  </div>
                  <div className="mt-1 font-display text-lg text-white uppercase leading-tight">
                    {d.name}
                  </div>
                  {d.bio && (
                    <div className="mt-1 font-body text-xs text-neutral-400 line-clamp-2">
                      {d.bio}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    data-testid={`admin-dj-edit-${d.id}`}
                    onClick={() => startEditDj(d)}
                    aria-label="Edit"
                    className="text-neutral-400 hover:text-gold p-2"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`admin-dj-delete-${d.id}`}
                    onClick={() => deleteDj(d.id)}
                    aria-label="Delete"
                    className="text-neutral-500 hover:text-crimson-live p-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Site settings */}
      <section
        data-testid="admin-settings-section"
        className="mt-8 bg-jet-surface border border-gold/20 rounded-lg overflow-hidden"
      >
        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
          <Settings className="w-4 h-4 text-gold" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            Site Settings
          </h2>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-2">
              Homepage news ticker speed
            </div>
            <div className="flex gap-2">
              {["slow", "normal", "fast"].map((s) => (
                <button
                  key={s}
                  data-testid={`admin-ticker-${s}`}
                  onClick={() => updateTickerSpeed(s)}
                  className={`px-4 py-2 rounded-full font-condensed uppercase tracking-widest text-[11px] transition-colors ${
                    settings.ticker_speed === s
                      ? "bg-gold text-black"
                      : "border border-gold/40 text-neutral-300 hover:border-gold hover:text-gold"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors */}
      <section
        data-testid="admin-sponsors-section"
        className="mt-8 bg-jet-surface border border-gold/20 rounded-lg overflow-hidden"
      >
        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-900">
          <Handshake className="w-4 h-4 text-gold" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            Sponsors ({sponsors.length})
          </h2>
        </div>
        <form onSubmit={addSponsor} className="p-4 grid md:grid-cols-4 gap-3 border-b border-neutral-900">
          <input
            data-testid="admin-sponsor-name"
            value={newSponsor.name}
            onChange={(e) => setNewSponsor({ ...newSponsor, name: e.target.value })}
            placeholder="Sponsor name"
            className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
          />
          <input
            data-testid="admin-sponsor-logo"
            value={newSponsor.logo_url}
            onChange={(e) => setNewSponsor({ ...newSponsor, logo_url: e.target.value })}
            placeholder="Logo image URL (optional)"
            className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none md:col-span-2"
          />
          <input
            data-testid="admin-sponsor-url"
            value={newSponsor.url}
            onChange={(e) => setNewSponsor({ ...newSponsor, url: e.target.value })}
            placeholder="Link URL (optional)"
            className="bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
          />
          <button
            data-testid="admin-sponsor-add"
            type="submit"
            className="md:col-span-4 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-5 py-2.5 rounded-md transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Sponsor
          </button>
        </form>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-neutral-900">
          {sponsors.length === 0 ? (
            <div className="p-6 text-center text-neutral-500 text-sm">
              No sponsors yet. Add the first one above — it'll rotate on the landing page.
            </div>
          ) : (
            sponsors.map((sp) => (
              <div
                key={sp.id}
                data-testid={`admin-sponsor-${sp.id}`}
                className="flex items-center gap-4 px-5 py-3 hover:bg-neutral-900/40"
              >
                <div className="w-14 h-14 shrink-0 rounded border border-neutral-800 bg-black grid place-items-center overflow-hidden">
                  {sp.logo_url ? (
                    <img src={sp.logo_url} alt={sp.name} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <span className="font-condensed uppercase text-[10px] text-neutral-500">No logo</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg text-white uppercase leading-tight truncate">
                    {sp.name}
                  </div>
                  {sp.url && (
                    <div className="font-body text-xs text-gold/80 truncate">{sp.url}</div>
                  )}
                  <div className="font-condensed uppercase tracking-widest text-[10px] mt-0.5">
                    <span className={sp.active ? "text-crimson-live" : "text-neutral-500"}>
                      {sp.active ? "● Active" : "○ Hidden"}
                    </span>
                  </div>
                </div>
                <button
                  data-testid={`admin-sponsor-toggle-${sp.id}`}
                  onClick={() => toggleSponsor(sp)}
                  aria-label={sp.active ? "Hide" : "Show"}
                  className="text-neutral-400 hover:text-gold"
                >
                  {sp.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  data-testid={`admin-sponsor-delete-${sp.id}`}
                  onClick={() => deleteSponsor(sp.id)}
                  aria-label="Delete sponsor"
                  className="text-neutral-500 hover:text-crimson-live"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

const AdminPage = () => {
  const { token, verifying } = useAdmin();

  if (verifying && !token) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-neutral-500">
        Checking session…
      </div>
    );
  }
  return token ? <AdminPanel /> : <LoginView />;
};

export default AdminPage;
